import { DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, ArrowUpRight, AlertCircle, ShieldCheck, Percent, Unlock } from "lucide-react";

const KEY_POINTS = [
  { key: "keyPointOwnership", icon: ShieldCheck, accent: "text-brand-blue" },
  { key: "keyPointCommission", icon: Percent, accent: "text-brand-red" },
  { key: "keyPointNoLockIn", icon: Unlock, accent: "text-brand-blue" },
];

const renderBold = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((chunk, i) =>
    chunk.startsWith("**") && chunk.endsWith("**") ? (
      <strong key={i} className="text-white font-bold">{chunk.slice(2, -2)}</strong>
    ) : (
      <span key={i}>{chunk}</span>
    )
  );

export const AgreementStep = ({
  t, scrollRef, onScroll, agreementBody, scrolledToEnd,
  agreed, onAgreedChange, submitError, rateLimited, sending, onAccept, onBack,
}) => (
  <div className="flex flex-col max-h-[85vh]">
    <DialogHeader className="p-6 pb-3 text-left">
      <button
        data-testid="agreement-back-button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-400 hover:text-white transition-colors duration-200 mb-2"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        {t.modal.backButton}
      </button>
      <DialogTitle className="font-heading text-lg font-bold text-white">{t.modal.agreementTitle}</DialogTitle>
      <DialogDescription className="text-neutral-400 text-xs leading-relaxed">{t.modal.agreementIntro}</DialogDescription>
    </DialogHeader>

    <div
      ref={scrollRef}
      onScroll={onScroll}
      data-testid="agreement-scroll-container"
      data-lenis-prevent
      className="mx-6 flex-1 overflow-y-auto rounded-xl border border-white/10 bg-white/[0.02] p-5"
      style={{ maxHeight: "55vh", minHeight: "220px" }}
    >
      <div data-testid="agreement-key-points" className="grid grid-cols-1 gap-2.5 mb-5">
        <p className="text-white text-xs font-bold uppercase tracking-wide mb-0.5">{t.modal.keyPointsTitle}</p>
        {KEY_POINTS.map(({ key, icon: Icon, accent }) => (
          <div
            key={key}
            data-testid={`agreement-key-point-${key.replace("keyPoint", "").toLowerCase()}`}
            className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3"
          >
            <Icon className={`w-4 h-4 ${accent} shrink-0 mt-0.5`} />
            <p className="text-neutral-300 text-xs leading-relaxed">{renderBold(t.modal[key])}</p>
          </div>
        ))}
      </div>
      <div data-testid="agreement-text-body" className="text-neutral-400 text-[12.5px] leading-relaxed whitespace-pre-wrap border-t border-white/10 pt-5">
        {agreementBody}
      </div>
    </div>

    <div className="p-6 pt-4 flex flex-col gap-4">
      {!scrolledToEnd && (
        <p data-testid="agreement-scroll-hint" className="text-brand-blue text-xs font-medium text-center">{t.modal.scrollHint}</p>
      )}
      <label className={`flex items-start gap-3 cursor-pointer ${!scrolledToEnd ? "opacity-40 pointer-events-none" : ""}`}>
        <Checkbox
          data-testid="agreement-accept-checkbox"
          checked={agreed}
          disabled={!scrolledToEnd}
          onCheckedChange={(v) => onAgreedChange(Boolean(v))}
          className="mt-0.5 border-white/30 data-[state=checked]:bg-brand-red data-[state=checked]:border-brand-red"
        />
        <span className="text-neutral-300 text-sm leading-relaxed">{t.modal.checkboxLabel}</span>
      </label>

      {submitError && (
        <div data-testid="agreement-submit-error" className="flex items-start gap-2.5 rounded-xl border border-brand-red/30 bg-brand-red/[0.06] p-3">
          <AlertCircle className="w-4 h-4 text-brand-red shrink-0 mt-0.5" />
          <p className="text-neutral-300 text-xs leading-relaxed">{rateLimited ? t.modal.rateLimitError : t.modal.submitError}</p>
        </div>
      )}

      <button
        type="button"
        data-testid="agreement-submit-button"
        onClick={onAccept}
        disabled={!agreed || sending}
        className="w-full bg-brand-red text-white font-bold text-sm py-3 rounded-full flex items-center justify-center gap-2 hover:-translate-y-0.5 hover:bg-brand-redDark shadow-[0_8px_24px_-6px_rgba(255,36,54,0.5)] transition-all duration-300 disabled:opacity-40 disabled:pointer-events-none disabled:translate-y-0"
      >
        {sending ? t.modal.sending : t.modal.acceptButton}
        {!sending && <ArrowUpRight className="w-4 h-4" />}
      </button>
    </div>
  </div>
);
